import {exec, spawn} from 'node:child_process';
import * as os from 'node:os';
import {promisify} from 'node:util';
import chalk from 'chalk';
import {detectShell} from '../../cli/commands/completion/utils.js';
import type {ExecContext, ExecResult} from '../../cli/commands/exec/types.js';

const execAsync = promisify(exec);

export interface ExecuteCommandOptions {
  command: string;
  shell?: string;
  cwd?: string;
  timeout?: number;
  dryRun?: boolean;
  interactive?: boolean;
  copyCommand?: boolean;
}

function getShellConfig(shell?: string): {shell: string; args: string[]} {
  const detectedShell = shell || detectShell();

  if (process.platform === 'win32') {
    switch (detectedShell) {
      case 'pwsh':
        return {shell: 'pwsh.exe', args: ['-NoProfile', '-Command']};
      case 'powershell':
        return {shell: 'powershell.exe', args: ['-NoProfile', '-Command']};
      case 'cmd':
        return {shell: 'cmd.exe', args: ['/c']};
      default:
        return {shell: 'pwsh.exe', args: ['-NoProfile', '-Command']};
    }
  } else {
    const shellPath = shell || process.env.SHELL || '/bin/bash';
    return {shell: shellPath, args: ['-c']};
  }
}

const INTERACTIVE_COMMANDS = [
  'btop',
  'htop',
  'top',
  'nano',
  'vim',
  'nvim',
  'emacs',
  'less',
  'more',
  'vi',
  'tmux',
  'screen',
  'watch',
  'tail -f',
  'journalctl -f',
  'docker exec -it',
  'kubectl exec -it',
  'ssh',
  'mysql',
  'psql',
  'mongo',
  'redis-cli',
  'python3 -i',
  'python -i',
  'node -i',
  'irb',
  'ghci',
];

function isInteractiveCommand(command: string): boolean {
  const trimmedCommand = command.trim().toLowerCase();
  return INTERACTIVE_COMMANDS.some(
    (interactiveCmd) =>
      trimmedCommand.startsWith(interactiveCmd) ||
      trimmedCommand.includes(` ${interactiveCmd}`),
  );
}

export async function executeCommand(
  options: ExecuteCommandOptions,
): Promise<ExecResult> {
  const startTime = Date.now();

  if (options.copyCommand) {
    setTimeout(() => {
      void copyToClipboard(options.command)
        .then(
          (copied) =>
            copied && console.log(chalk.dim('📋 Command copied to clipboard')),
        )
        .catch(() => {});
    }, 0);
  }

  if (options.dryRun) {
    return {
      command: options.command,
      stdout: '[DRY RUN] Command would be executed here',
      stderr: undefined,
      exitCode: 0,
      executionTime: Date.now() - startTime,
      success: true,
    };
  }

  const needsInteractive =
    options.interactive || isInteractiveCommand(options.command);

  if (needsInteractive) {
    return await executeInteractiveCommand(options, startTime);
  }

  try {
    if (options.command.includes('ssh-add')) {
      await ensureSSHAgent();
    }

    const shellConfig = getShellConfig(options.shell);

    const execOptions: any = {
      cwd: options.cwd || process.cwd(),
      timeout: options.timeout || 30000,
      maxBuffer: 1024 * 1024,
      encoding: 'utf8',
    };

    if (process.platform === 'win32') {
      execOptions.shell = 'cmd.exe';
      execOptions.windowsHide = true;
    } else {
      execOptions.shell = shellConfig.shell;
    }

    const {stdout, stderr} = await execAsync(options.command, execOptions);
    const executionTime = Date.now() - startTime;

    return {
      command: options.command,
      stdout: stdout?.toString() || undefined,
      stderr: stderr?.toString() || undefined,
      exitCode: 0,
      executionTime,
      success: true,
    };
  } catch (error: any) {
    const executionTime = Date.now() - startTime;

    return {
      command: options.command,
      stdout: error.stdout || undefined,
      stderr: error.stderr || error.message,
      exitCode: error.code || 1,
      executionTime,
      success: false,
    };
  }
}

async function executeInteractiveCommand(
  options: ExecuteCommandOptions,
  startTime: number,
): Promise<ExecResult> {
  return new Promise((resolve) => {
    const shellConfig = getShellConfig(options.shell);

    let child: any;
    if (process.platform === 'win32') {
      child = spawn('cmd.exe', ['/c', options.command], {
        stdio: 'inherit',
        cwd: options.cwd || process.cwd(),
        windowsHide: false,
      });
    } else {
      child = spawn(shellConfig.shell, ['-c', options.command], {
        stdio: 'inherit',
        cwd: options.cwd || process.cwd(),
      });
    }

    child.on('exit', (code: number | null) => {
      const executionTime = Date.now() - startTime;

      resolve({
        command: options.command,
        stdout: undefined,
        stderr: code !== 0 ? `Process exited with code ${code}` : undefined,
        exitCode: code || 0,
        executionTime,
        success: code === 0,
      });
    });

    child.on('error', (error: Error) => {
      const executionTime = Date.now() - startTime;

      resolve({
        command: options.command,
        stdout: undefined,
        stderr: error.message,
        exitCode: 1,
        executionTime,
        success: false,
      });
    });
  });
}

async function ensureSSHAgent(): Promise<void> {
  if (process.env.SSH_AUTH_SOCK) {
    console.log('🔑 SSH agent is already running.');
    return;
  }

  console.log('🚀 Starting SSH agent...');
  try {
    const {stdout} = await execAsync('ssh-agent -s');
    stdout.split('\n').forEach((line) => {
      const match = line.match(/^(SSH_AUTH_SOCK|SSH_AGENT_PID)=(.*?);/);
      if (match) {
        process.env[match[1]] = match[2];
      }
    });

    if (!process.env.SSH_AUTH_SOCK) {
      throw new Error('Failed to start ssh-agent');
    }
  } catch (sshError) {
    console.warn('Warning: Could not start SSH agent:', sshError);
  }
}

export function formatExecutionResult(result: ExecResult): string {
  const lines: string[] = [];

  lines.push(`🤖 Command: ${result.command}`);
  lines.push(`⏱️  Execution time: ${result.executionTime}ms`);

  if (result.success) {
    lines.push('✅ Success');
    if (result.stdout) {
      lines.push('📄 Output:');
      lines.push(result.stdout);
    }
  } else {
    lines.push(`❌ Failed (exit code: ${result.exitCode})`);
    if (result.stderr) {
      lines.push('⚠️ Error:');
      lines.push(result.stderr);
    }
  }

  return lines.join('\n');
}

export async function generateCommandContext(): Promise<ExecContext> {
  let directoryListing = '';
  try {
    if (process.platform === 'win32') {
      const {stdout} = await execAsync('dir /b', {encoding: 'utf8'});
      directoryListing = stdout;
    } else {
      const {stdout} = await execAsync('ls', {encoding: 'utf8'});
      directoryListing = stdout;
    }
  } catch (_) {
    directoryListing = 'Unable to get directory listing';
  }

  return {
    os: {
      type: os.type(),
      release: os.release(),
      platform: os.platform(),
      arch: os.arch(),
    },
    node: {
      version: process.version,
    },
    shell: process.env.SHELL || detectShell() || 'unknown',
    cwd: process.cwd(),
    home: os.homedir(),
    cpu: {
      model: os.cpus()[0]?.model || 'unknown',
      cores: os.cpus().length,
    },
    memory: {
      total: Math.floor(os.totalmem() / 1024 / 1024),
      free: Math.floor(os.freemem() / 1024 / 1024),
    },
    directoryListing,
  };
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text || text.trim() === '') {
    return false;
  }

  const fs = await import('node:fs/promises');
  const path = await import('node:path');
  const os = await import('node:os');
  const {execSync} = await import('node:child_process');

  const tempFile = path.join(os.tmpdir(), `clipboard-${Date.now()}.txt`);

  try {
    await fs.writeFile(tempFile, text, {flag: 'w'});

    if (process.platform === 'darwin') {
      execSync(`pbcopy < "${tempFile}"`, {stdio: 'ignore'});
    } else if (process.platform === 'win32') {
      execSync(`type "${tempFile}" | clip`, {stdio: 'ignore'});
    } else {
      try {
        execSync(`xclip -selection clipboard < "${tempFile}"`, {
          stdio: 'ignore',
        });
      } catch {
        execSync(`xsel --clipboard --input < "${tempFile}"`, {stdio: 'ignore'});
      }
    }

    return true;
  } catch {
    return false;
  } finally {
    void fs.unlink(tempFile).catch(() => {});
  }
}

export function sanitizeCommandOutput(
  output: string,
  options: {
    maxLength?: number;
    removeAnsi?: boolean;
    extractCodeBlock?: boolean;
  } = {},
): string {
  if (!output) return '';

  const {
    maxLength = 10000,
    removeAnsi = true,
    extractCodeBlock = false,
  } = options;

  let result =
    output.length > maxLength
      ? `${output.substring(0, maxLength)}... (truncated)`
      : output;

  if (removeAnsi) {
    const escapeChar = String.fromCharCode(27);
    const ansiRegex = new RegExp(`${escapeChar}\\[\\d+m`, 'g');
    result = result.replace(ansiRegex, '');
  }

  if (extractCodeBlock) {
    const codeBlockRegex = /```(?:[^\n]*)\n([\s\S]*?)```/g;
    let lastCodeBlock: string | null = null;

    const matches = [...result.matchAll(codeBlockRegex)];
    if (matches.length > 0) {
      const lastMatch = matches[matches.length - 1];
      if (lastMatch[1]) {
        lastCodeBlock = lastMatch[1];
      }
    }

    if (lastCodeBlock) {
      result = lastCodeBlock;
    }
  }

  return result;
}

export function isPotentiallyDangerousCommand(command: string): boolean {
  const dangerousPatterns = [
    /\brm\s+(-rf?|--recursive|--force)\b.*\//,
    /\bdd\s+.*of=/,
    /\bmkfs\b/,
    /\bformat\b/,
    /\bshred\b/,
    /\b:(){:|:&};:\b/,
    /\bchmod\s+-R\s+777\b/,
    /\b>.*\/(etc|var|usr)\//,
    /\bwget\s+.*\s+\|\s+bash\b/,
    /\bcurl\s+.*\s+\|\s+bash\b/,
  ];

  return dangerousPatterns.some((pattern) =>
    pattern.test(command.toLowerCase()),
  );
}
